import React, { useState } from 'react';
import { Mail, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Clock, CircleAlert as AlertCircle, CircleCheck as CheckCircle, Loader as Loader2, FileSpreadsheet, ArrowLeft } from 'lucide-react';
import { useIssueEmailHistory } from '../../hooks/useIssueEmailHistory';
import { IssueTypeBadge, StatusBadge } from './IssueTypeBadge';
import type { OrderIssueType, OrderIssueStatus, IssueEmailLogDetail } from '../../types/orderIssues';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const IssueEmailHistory: React.FC = () => {
  const {
    logs,
    loading,
    total,
    page,
    pageSize,
    setPage,
    detail,
    detailLoading,
    loadLogDetail,
    clearDetail,
  } = useIssueEmailHistory();

  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleToggleExpand = (logId: string) => {
    if (expandedLogId === logId) {
      setExpandedLogId(null);
      clearDetail();
    } else {
      setExpandedLogId(logId);
      loadLogDetail(logId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <Mail className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          No emails sent yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
          Email history will appear here once scheduled or manual emails have been sent.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs.map((log) => {
        const isExpanded = expandedLogId === log.id;
        const isSent = log.status === 'sent';

        return (
          <div
            key={log.id}
            className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800"
          >
            <button
              onClick={() => handleToggleExpand(log.id)}
              className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isSent
                  ? 'bg-emerald-100 dark:bg-emerald-900/30'
                  : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                {isSent ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                    {isSent ? 'Email Sent' : 'Email Failed'}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    log.trigger_type === 'scheduled'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  }`}>
                    {log.trigger_type === 'scheduled' ? 'Scheduled' : 'Manual'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <FileSpreadsheet className="h-3 w-3" />
                    {log.issue_count} issue{log.issue_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(log.created_at)}
                  </span>
                  <span className="truncate">
                    To: {log.recipient_emails.join(', ')}
                  </span>
                </div>
              </div>

              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 dark:border-gray-700/50">
                {log.error_message && (
                  <div className="mx-4 mt-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40">
                    {log.error_message}
                  </div>
                )}

                {detailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 text-amber-500 animate-spin" />
                  </div>
                ) : detail && detail.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50/80 dark:bg-gray-800/60">
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Order
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                            Error
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                            Current Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.map((d, idx) => (
                          <tr
                            key={d.issue_id || idx}
                            className="border-t border-gray-100 dark:border-gray-700/50"
                          >
                            <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                              {d.order_reference || '-'}
                            </td>
                            <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                              {d.client_name || '-'}
                            </td>
                            <td className="px-4 py-2.5">
                              {d.issue_type ? (
                                <IssueTypeBadge type={d.issue_type as OrderIssueType} />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 max-w-xs hidden md:table-cell">
                              <p className="text-gray-600 dark:text-gray-400 text-xs truncate" title={d.issue_description || ''}>
                                {d.issue_description || '-'}
                              </p>
                            </td>
                            <td className="px-4 py-2.5 hidden lg:table-cell">
                              {d.issue_status ? (
                                <StatusBadge status={d.issue_status as OrderIssueStatus} />
                              ) : (
                                <span className="text-xs text-gray-400">Deleted</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : detail && detail.length === 0 ? (
                  <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                    No issue details available for this batch.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}

      {total > pageSize && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
